// smoothDampRuns — the per-node drive loop behind the Smooth Damp event node.
//
// The event graph is EDGE-TRIGGERED: there is no per-frame tick event and no dt
// to integrate against. So this node self-drives, the way `delay` self-resumes
// on a setTimeout — on fire it starts its own frame loop, steps `smoothDamp`
// once per frame, writes the result into an animation variable, and stops when
// the value settles. The carried velocity lives here, across frames, keyed by
// node id (runtime-only, never in node.data, so a reload starts clean — the same
// lifecycle as _rendezvousState).
//
// Everything the loop needs from the graph arrives as CLOSURES (`target`,
// `smoothTime`, `read`, `isStale`), which is what makes the node track a MOVING
// target: the target pin is re-resolved every frame, not captured at fire time.
// It is also what keeps this module free of EventRuntime, so the loop is
// unit-testable against an injected clock instead of a real rAF.
//
// The value lives HERE, in the run — the node interpolates between two numbers
// and publishes the result on its own `out-value` pin (`valueOf`), the way
// Unreal's FInterpTo returns a value rather than writing a variable. `write` is
// therefore OPTIONAL: a caller that also wants the number mirrored somewhere
// passes one, and nothing in this module requires it.
//
// Re-firing a running node RETARGETS it rather than starting a second run: two
// loops writing one variable would fight every frame, and the surviving value
// would be whichever wrote last. Retargeting keeps the velocity, so a target
// that changes mid-flight is followed smoothly instead of restarting from rest.
//
// Unit-tested in tests/unit/smoothDampRuns.test.mjs.

import { smoothDamp } from './smoothDamp.js'

/**
 * Longest step the integrator will take, seconds. A backgrounded tab delivers
 * one multi-second frame on return; the approximation is bounded so it cannot
 * blow up, but without this the value teleports to the target on that frame.
 * Clamping means it resumes from where it was, slightly behind.
 */
export const MAX_STEP_S = 0.1

/**
 * @param {{now:()=>number, requestFrame:(cb:(t:number)=>void)=>any, cancelFrame:(h:any)=>void}} clock
 */
export function createSmoothDampRunner(clock) {
  /** @type {Map<string, any>} */
  const runs = new Map()
  // Last value each node wrote, kept AFTER its run ends. The node's `out-value`
  // pin is read by whatever its Finished chain runs, and by then the run is
  // gone — so a live-only lookup would hand the completion chain a null for the
  // very value it just finished computing.
  /** @type {Map<string, number>} */
  const lastValue = new Map()
  // What the last run DID, kept after it ends. A run that starts already on its
  // target settles in ONE tick and moves nothing — correct, and on screen
  // identical to a node that is broken. The editor reads this to say which it
  // was; without it the only honest answer available to the user is silence.
  /** @type {Map<string, {value:number, target:number, ticks:number, noop:boolean}>} */
  const lastRun = new Map()

  function schedule(run) {
    run.handle = clock.requestFrame(() => { run.handle = null; tick(run) })
  }

  function finish(run, done) {
    if (done) {
      lastRun.set(run.id, {
        value: run.value,
        target: run.target_,
        ticks: run.ticks,
        // Nothing to interpolate: it was already there when it started.
        noop: run.ticks <= 1 && Math.abs(run.startValue - run.target_) <= run.epsilon,
      })
    }
    runs.delete(run.id)
    if (run.handle != null) { clock.cancelFrame(run.handle); run.handle = null }
    if (done) run.onDone?.()
  }

  function tick(run) {
    // The chain that started this run was interrupted (stopSpeech bumped the
    // exec epoch): stop where we are and do NOT fire the completion chain —
    // same contract as a delay whose epoch moved on.
    if (run.isStale?.()) { finish(run, false); return }

    const t = clock.now()
    const dt = Math.min(MAX_STEP_S, Math.max(0, (t - run.lastT) / 1000))
    run.lastT = t
    run.ticks++

    const target = Number(run.target())
    if (!Number.isFinite(target)) {
      // A pin that resolves to nothing numeric would drive the variable to NaN
      // and leave the loop spinning forever. Stop, loudly, and don't pretend the
      // move completed.
      run.onError?.(`target is not a number (${JSON.stringify(run.target())})`)
      finish(run, false)
      return
    }

    const smoothTime = Number(run.smoothTime())
    const r = smoothDamp(
      run.value, target, run.velocity,
      Number.isFinite(smoothTime) ? smoothTime : 0,
      dt,
      { maxSpeed: run.maxSpeed, epsilon: run.epsilon },
    )
    run.target_ = target        // last resolved target, for the debug seam

    if (r.done) {
      // LAND ON THE TARGET, exactly. The approach is asymptotic, so `done` is a
      // threshold (|gap| <= epsilon) and the last stepped value is merely CLOSE
      // — measured in a browser, a run to 1 stopped at 0.99912 and stayed there
      // for good, because the loop then ends. A blend alpha parked a thousandth
      // short of fully blended is a defect that nothing on screen explains.
      run.value = target
      run.velocity = 0
      run.write?.(target)
      lastValue.set(run.id, target)
      run.onProgress?.(target)   // the landing IS a tick — Progress sees the last value too
      finish(run, true)
      return
    }

    run.value = r.value
    run.velocity = r.velocity
    run.write?.(r.value)
    lastValue.set(run.id, r.value)
    // Progress: fired AFTER the write, so anything it runs reads the value this
    // frame produced rather than the previous one.
    run.onProgress?.(r.value)
    schedule(run)
  }

  return {
    /**
     * Start smoothing, or retarget an already-running node (velocity kept).
     * @param {string} nodeId
     * @param {{read:()=>number, target:()=>any, smoothTime:()=>any,
     *          write?:(v:number)=>void, maxSpeed?:number, epsilon?:number,
     *          isStale?:()=>boolean, onProgress?:(v:number)=>void,
     *          onDone?:()=>void, onError?:(m:string)=>void}} opts
     */
    start(nodeId, opts) {
      const existing = runs.get(nodeId)
      if (existing) {
        // Retarget: adopt the new closures and knobs, keep value + velocity.
        Object.assign(existing, opts, { id: nodeId, handle: existing.handle, value: existing.value, velocity: existing.velocity })
        // A retarget is a NEW request: the travel is measured from here, so a
        // long first leg can't make a second, no-op leg look like it moved.
        existing.startValue = existing.value
        existing.ticks = 0
        existing.maxSpeed = opts.maxSpeed ?? 0
        existing.epsilon  = opts.epsilon ?? 0.001
        if (existing.handle == null) { existing.lastT = clock.now(); schedule(existing) }
        return existing
      }
      const start = Number(opts.read())
      const run = {
        ...opts,
        id: nodeId,
        maxSpeed: opts.maxSpeed ?? 0,
        epsilon: opts.epsilon ?? 0.001,
        value: Number.isFinite(start) ? start : 0,
        startValue: Number.isFinite(start) ? start : 0,
        ticks: 0,
        velocity: 0,
        target_: null,
        lastT: clock.now(),
        handle: null,
      }
      runs.set(nodeId, run)
      schedule(run)
      return run
    },

    /** Cancel a run where it stands. No completion chain. */
    stop(nodeId) {
      const run = runs.get(nodeId)
      if (!run) return false
      finish(run, false)
      return true
    },

    /** Cancel every run (stopSpeech, character switch, graph teardown). */
    stopAll() {
      for (const id of [...runs.keys()]) this.stop(id)
    },

    /**
     * The node's current value: live while it runs, else the last value it
     * wrote. `null` for a node that has never run — absence, not a made-up 0.
     */
    valueOf(nodeId) {
      const run = runs.get(nodeId)
      if (run) return run.value
      return lastValue.has(nodeId) ? lastValue.get(nodeId) : null
    },

    /**
     * What the node's last COMPLETED run did — `{value, target, ticks, noop}`,
     * or null if it has never finished one. `noop` means it began on its target
     * and therefore moved nothing.
     */
    resultOf(nodeId) {
      return lastRun.get(nodeId) ?? null
    },

    /** Debug/test seam — what is smoothing right now. */
    list() {
      return [...runs.values()].map(r => ({
        nodeId: r.id, varName: r.varName ?? null,
        value: r.value, velocity: r.velocity, target: r.target_,
      }))
    },
  }
}
